import React from 'react';
import { Box, Typography, Paper, Divider, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'text.primary', mb: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Last Updated: October 3, 2025
        </Typography>
      </Box>

      <Paper sx={{ p: 4, backgroundColor: 'background.paper' }}>
        <Box sx={{ maxWidth: '800px' }}>
          
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            TextWiz ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains 
            how we collect, use, disclose, and safeguard your information when you use our Service.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            1. Information We Collect
          </Typography>
          
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', mt: 2 }}>
            Account Information
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Email address (via AWS Cognito authentication)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Account credentials and authentication tokens
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                User ID and account creation date
              </Typography>
            </li>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', mt: 2 }}>
            Content Data
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                EPUB books you upload
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Book metadata (titles, authors, file names)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Book content and text excerpts for AI processing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                AI-generated images and associated metadata
              </Typography>
            </li>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', mt: 2 }}>
            Payment Information
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Payment processing is handled by Stripe; we do not store credit card information
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Transaction history and credit balance
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                Purchase receipts and billing information
              </Typography>
            </li>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', mt: 2 }}>
            Usage Information
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Service usage patterns and feature interactions
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Processing status and error logs
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                API usage and performance metrics
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            2. How We Use Your Information
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Service Provision:</strong> To process your books, generate images, and provide the core functionality of TextWiz
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Authentication:</strong> To verify your identity and manage your account
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Billing:</strong> To process payments and manage your credit balance
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Service Improvement:</strong> To analyze usage patterns and improve our features
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Customer Support:</strong> To respond to your questions and resolve issues
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                <strong>Legal Compliance:</strong> To comply with applicable laws and regulations
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            3. Third-Party Services
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}>
            We use the following third-party services that may access your data:
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>AWS (Amazon Web Services):</strong> Cloud infrastructure for storage (S3), databases (DynamoDB), and computing (Lambda)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>AWS Cognito:</strong> User authentication and account management
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Stripe:</strong> Payment processing (subject to Stripe's privacy policy)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>OpenAI:</strong> AI text analysis and processing (subject to OpenAI's privacy policy)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                <strong>Replicate:</strong> AI image generation (subject to Replicate's privacy policy)
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            4. Data Storage and Security
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Your data is stored on AWS servers in secure, encrypted storage
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Books and images are stored in Amazon S3 with access controls
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Account and metadata are stored in Amazon DynamoDB
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                We use JWT authentication tokens and presigned URLs for secure access
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                All data transmission is encrypted using HTTPS/TLS
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            5. Data Retention
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Account Data:</strong> Retained as long as your account is active
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Books and Images:</strong> Retained until you delete them or close your account
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Transaction Records:</strong> Retained for legal and accounting purposes (typically 7 years)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                <strong>Deleted Data:</strong> Permanently removed from our systems within 30 days of deletion
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            6. Cookies and Tracking
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}>
            We use essential cookies for:
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Authentication:</strong> To keep you logged in and manage your session
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Security:</strong> To protect against unauthorized access and CSRF attacks
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                <strong>Preferences:</strong> To remember your settings and cookie consent choices
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            We do not use third-party advertising cookies or tracking pixels.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            7. Your Privacy Rights
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}>
            Depending on your location, you may have the following rights:
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Access:</strong> Request a copy of your personal data
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Correction:</strong> Update or correct inaccurate information
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Deletion:</strong> Request deletion of your account and associated data
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Portability:</strong> Receive your data in a machine-readable format
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Objection:</strong> Object to certain data processing activities
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                <strong>Withdraw Consent:</strong> Revoke previously given consent
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            To exercise these rights, please use the account deletion feature in your Account settings or contact us.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            8. GDPR Compliance (EU Users)
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            If you are located in the European Economic Area (EEA), you have additional rights under the General Data 
            Protection Regulation (GDPR). We process your data based on your consent and to fulfill our contract with you. 
            You have the right to lodge a complaint with your local data protection authority.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            9. CCPA Compliance (California Users)
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA), including 
            the right to know what personal information we collect, the right to delete your information, and the right to 
            opt-out of the sale of your information. We do not sell your personal information.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            10. Children's Privacy
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            Our Service is not intended for users under the age of 18. We do not knowingly collect information from 
            children. If you believe we have collected information from a child, please contact us immediately.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            11. International Data Transfers
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            Your data may be transferred to and processed in countries other than your own. We ensure appropriate 
            safeguards are in place to protect your data in accordance with this Privacy Policy.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            12. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by posting 
            a notice on our Service or sending you an email. Your continued use of the Service after changes constitutes 
            acceptance of the updated policy.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            13. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            If you have questions about this Privacy Policy or how we handle your data, please contact us through 
            your Account page or at your designated support email.
          </Typography>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              By using TextWiz, you acknowledge that you have read and understood this Privacy Policy.
            </Typography>
          </Box>

        </Box>
      </Paper>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Link
          component="button"
          onClick={() => navigate(-1)}
          sx={{ color: 'primary.light', textDecoration: 'underline', cursor: 'pointer' }}
        >
          ← Back
        </Link>
      </Box>
    </Box>
  );
};

export default PrivacyPolicyPage;

